import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppIcon } from "./AppIcon";
import { useVault } from "../app/context/VaultContext";
import { usePins } from "../app/context/PinsContext";
import { useLongPress, type LongPressCoords } from "../hooks/useLongPress";
import { VaultGlyphIcon } from "./VaultGlyphIcon";
import { SidebarContextMenu } from "./SidebarContextMenu";
import type { VaultWire } from "../lib/authApi";

type VaultSectionProps = {
  selectedVaultId: string | null;
  onSelectVault: (vaultId: string) => void;
  /** Вызывается после lock — сбросить открытый vault в чате. */
  onVaultLock: () => void;
  isMobile: boolean;
  embedded?: boolean;
  collapsible?: boolean;
  expanded?: boolean;
  onToggleExpanded?: () => void;
};

type VaultPinInputProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
};

function VaultPinInput({ id, label, value, onChange, onSubmit }: VaultPinInputProps) {
  return (
    <div className="vault-section__pin-field">
      <label className="vault-section__label" htmlFor={id}>
        {label}
      </label>
      <div className="vault-section__pin-shell">
        <input
          id={id}
          className="vault-section__pin-input"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="done"
          pattern="[0-9]*"
          maxLength={5}
          name={`${id}-local-pin`}
          data-1p-ignore="true"
          data-lpignore="true"
          data-form-type="other"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 5))}
          onKeyDown={(e) => {
            if (e.key === "Enter" && value.length === 5) {
              onSubmit?.();
            }
          }}
          aria-label={label}
        />
        <div className="vault-section__pin-slots" aria-hidden="true">
          {Array.from({ length: 5 }, (_, index) => {
            const filled = index < value.length;
            const active = index === value.length && value.length < 5;
            return (
              <span
                key={index}
                className={`vault-section__pin-slot${filled ? " is-filled" : ""}${active ? " is-active" : ""}`}
              >
                {filled ? "•" : ""}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function VaultSection({
  selectedVaultId,
  onSelectVault,
  onVaultLock,
  isMobile: _isMobile,
  embedded = false,
  collapsible = false,
  expanded = true,
  onToggleExpanded,
}: VaultSectionProps) {
  const {
    hasPin,
    unlocked,
    vaults,
    loading,
    pinError,
    setInitialPinAndFirstVault,
    verifyPinAndUnlock,
    lock,
    touchActivity,
    createVault,
  } = useVault();

  const { isPinned, getPinnedAt } = usePins();

  const sortedVaults = useMemo(() => {
    const list = [...vaults];
    list.sort((a, b) => {
      const ap = isPinned("vault", a.id) ? 1 : 0;
      const bp = isPinned("vault", b.id) ? 1 : 0;
      if (ap !== bp) return bp - ap;
      if (ap && bp) return (getPinnedAt("vault", a.id) ?? 0) - (getPinnedAt("vault", b.id) ?? 0);
      return 0;
    });
    return list;
  }, [vaults, isPinned, getPinnedAt]);

  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; vaultId: string } | null>(null);
  const onLongPressVault = useCallback(
    (vaultId: string) => (coords: LongPressCoords) => {
      setCtxMenu({ ...coords, vaultId });
    },
    [],
  );

  const [pinDraft, setPinDraft] = useState("");
  const [addName, setAddName] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const handleLock = useCallback(() => {
    onVaultLock();
    lock();
  }, [lock, onVaultLock]);

  const handleCreateFirstVault = useCallback(() => {
    if (pinDraft.length !== 5) return;
    void setInitialPinAndFirstVault(pinDraft).then(() => setPinDraft(""));
  }, [pinDraft, setInitialPinAndFirstVault]);

  const unlockInFlightRef = useRef(false);

  const runUnlockAttempt = useCallback(() => {
    if (pinDraft.length !== 5 || loading || unlockInFlightRef.current) return;
    unlockInFlightRef.current = true;
    void verifyPinAndUnlock(pinDraft)
      .then(() => {
        setPinDraft("");
      })
      .finally(() => {
        unlockInFlightRef.current = false;
      });
  }, [pinDraft, loading, verifyPinAndUnlock]);

  useEffect(() => {
    if (!hasPin || unlocked || pinDraft.length !== 5 || loading) return;
    runUnlockAttempt();
  }, [hasPin, unlocked, pinDraft, loading, runUnlockAttempt]);

  const handleUnlock = runUnlockAttempt;

  const titleText = "// ENCRYPTED VAULTS";

  const headTitle = collapsible ? (
    <button
      type="button"
      className="workspace-nav__head-toggle vault-section__head-toggle"
      onClick={onToggleExpanded}
      aria-expanded={expanded}
    >
      <span className="workspace-nav__title vault-section__title" title={titleText}>
        {titleText}
      </span>
      <AppIcon
        name={expanded ? "chevron-down" : "chevron-up"}
        size={19}
        className="sidebar-section__chevron"
      />
    </button>
  ) : (
    <span className="workspace-nav__title vault-section__title" title={titleText}>
      {titleText}
    </span>
  );

  const headRightUnlocked =
    hasPin && unlocked ? (
      <div className="workspace-nav__head-right sidebar-section__head-actions">
        <span className="sidebar-section__meta">{vaults.length}</span>
        <button
          type="button"
          className="sidebar-section__action-btn vault-section__icon-btn"
          title="Новое хранилище"
          aria-label="Новое хранилище"
          onClick={() => {
            touchActivity();
            setAddOpen((v) => !v);
            setAddName("");
          }}
        >
          <AppIcon name="plus" size={16} className="sidebar-section__action-icon" />
        </button>
        <button
          type="button"
          className="sidebar-section__action-btn vault-section__icon-btn"
          title="Заблокировать"
          aria-label="Заблокировать"
          onClick={handleLock}
        >
          <AppIcon name="lock-keyhole-open" size={16} className="sidebar-section__action-icon" />
        </button>
      </div>
    ) : null;

  const headRightLocked =
    hasPin && !unlocked ? (
      <span className="vault-section__locked-mark" aria-hidden title="Заблокировано">
        <AppIcon name="lock-keyhole" size={16} className="sidebar-section__action-icon" />
      </span>
    ) : null;

  const mainContent = (
    <>
      {!hasPin ? (
        <div className="vault-section__panel">
          <VaultPinInput
            id="vault-pin-init"
            label="Задайте пин-код"
            value={pinDraft}
            onChange={setPinDraft}
            onSubmit={handleCreateFirstVault}
          />
          {pinError ? <p className="vault-section__err">{pinError}</p> : null}
          <button
            type="button"
            className="vault-section__primary"
            disabled={loading || pinDraft.length !== 5}
            onClick={handleCreateFirstVault}
          >
            Создать хранилище
          </button>
        </div>
      ) : null}

      {hasPin && !unlocked ? (
        <div className="vault-section__panel">
          <VaultPinInput
            id="vault-pin-enter"
            label="Введите пин-код"
            value={pinDraft}
            onChange={setPinDraft}
            onSubmit={handleUnlock}
          />
          {pinError ? <p className="vault-section__err">{pinError}</p> : null}
        </div>
      ) : null}

      {hasPin && unlocked ? (
        <>
          {addOpen ? (
            <form
              className="workspace-nav__create-form vault-section__add-form"
              onSubmit={(e) => {
                e.preventDefault();
                const n = addName.trim();
                if (!n || loading) return;
                void createVault(n).then(() => {
                  setAddName("");
                  setAddOpen(false);
                  touchActivity();
                });
              }}
            >
              <input
                className="workspace-nav__create-input"
                type="text"
                placeholder="имя хранилища…"
                autoComplete="off"
                maxLength={128}
                value={addName}
                disabled={loading}
                onChange={(e) => setAddName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setAddOpen(false);
                    setAddName("");
                  }
                }}
              />
              <button type="submit" className="workspace-nav__create-ok" disabled={!addName.trim() || loading}>
                OK
              </button>
            </form>
          ) : null}
          <ul className="contact-list vault-section__list">
            {sortedVaults.map((v) => (
              <VaultRow
                key={v.id}
                vault={v}
                isSelected={v.id === selectedVaultId}
                pinned={isPinned("vault", v.id)}
                onSelect={() => { touchActivity(); onSelectVault(v.id); }}
                onLongPress={onLongPressVault(v.id)}
              />
            ))}
          </ul>
          {ctxMenu ? (
            <SidebarContextMenu
              x={ctxMenu.x}
              y={ctxMenu.y}
              mode="personal"
              entityType="vault"
              entityId={ctxMenu.vaultId}
              onClose={() => setCtxMenu(null)}
            />
          ) : null}
        </>
      ) : null}
    </>
  );

  const body = (
    <div className="sidebar-section sidebar-section--vaults">
      <div className={`workspace-nav__head sidebar-section__toolbar vault-section__head ${collapsible ? "workspace-nav__head--collapsible vault-section__head--collapsible" : ""}`}>
        {headTitle}
        {headRightUnlocked}
        {headRightLocked}
      </div>

      {collapsible ? (
        <div className={`sidebar-section__body-wrap ${expanded ? "is-expanded" : ""}`}>
          <div className="sidebar-section__body-inner">{mainContent}</div>
        </div>
      ) : (
        mainContent
      )}
    </div>
  );

  if (embedded) return body;
  return <div className="sidebar-inner sidebar-inner--vaults">{body}</div>;
}

function VaultRow({
  vault: v,
  isSelected,
  pinned,
  onSelect,
  onLongPress,
}: {
  vault: VaultWire;
  isSelected: boolean;
  pinned: boolean;
  onSelect: () => void;
  onLongPress: (coords: LongPressCoords) => void;
}) {
  const lp = useLongPress(onLongPress);
  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        className={`contact-row vault-section__row ${isSelected ? "is-selected" : ""}`}
        onClick={onSelect}
        onKeyDown={(e) => { if (e.key === "Enter") onSelect(); }}
        {...lp}
      >
        <div className="contact-avatar vault-section__avatar" aria-hidden>
          <VaultGlyphIcon className="vault-section__avatar-glyph" />
        </div>
        <div className="contact-row__info">
          <span className="contact-row__name">
            {pinned ? <AppIcon name="pin" size={12} className="contact-row__pin-icon" /> : null}
            {v.name}
          </span>
        </div>
      </div>
    </li>
  );
}
